// src/pages/AddProductPage.tsx - VERSION COMPLÈTE avec delivery_fee
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import productService, { ProductCreate } from '../services/productService';
import categoryService, { Category } from '../services/categoryService';

// Using Category type imported from services/categoryService

interface ProductTemplate {
  id: number;
  name: string;
}

const AddProductPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // ✅ MODIFIÉ - Ajouter delivery_fee dans formData
  const [formData, setFormData] = useState<ProductCreate>({
    name: '',
    description: '',
    price: 0,
    category_id: 0,
    stock: 0,
    delivery_fee: 0  // ✅ NOUVEAU CHAMP
  });
  
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [productTemplates, setProductTemplates] = useState<ProductTemplate[]>([]);

  // Vérifier que l'utilisateur est un farmer
  useEffect(() => {
    if (user && !user.is_farmer) {
      navigate('/');
    }
  }, [user, navigate]);

  // Charger les catégories depuis l'API avec service professionnel
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const categoriesData = await categoryService.getCategories();
        
        // Valider et filtrer les catégories
        const validCategories = categoriesData.filter(cat => 
          categoryService.validateCategory(cat)
        );
        
        setCategories(validCategories);
        
        if (validCategories.length === 0) {
          setErrors(['Aucune catégorie disponible. Contactez l\'administrateur.']);
        }

        try {
          const res = await fetch('/api/product-templates/');
          const data = await res.json();
          setProductTemplates(data);
        } catch (e) {
          console.error('Erreur chargement templates:', e);
        }

      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
        setErrors(['Erreur lors du chargement des catégories. Utilisation des catégories par défaut.']);
        
        // Fallback vers catégories hardcodées en cas d'erreur critique
        setCategories([
          { id: 1, name: 'Céréales' },
          { id: 2, name: 'Légumes' },
          { id: 3, name: 'Fruits' }
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // ✅ MODIFIÉ - Inclure delivery_fee dans les champs numériques
    const numericValue = (name === 'price' || name === 'stock' || name === 'category_id' || name === 'delivery_fee') 
      ? parseFloat(value) || 0 
      : value;
    
    setFormData({
      ...formData,
      [name]: numericValue
    });
    
    // Effacer les erreurs quand l'utilisateur tape
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Valider l'image
      const imageErrors = productService.validateImageFile(file);
      if (imageErrors.length > 0) {
        setErrors(imageErrors);
        return;
      }
      
      setImage(file);
      setErrors([]);
      
      // Créer un aperçu de l'image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSuccess('');
    setLoading(true);

    try {
      // Validation des données
      const validationErrors = productService.validateProductData(formData);
      
      if (!image) {
        validationErrors.push('Veuillez sélectionner une image du produit');
      }
      
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        return;
      }

      // Appel API pour créer le produit
      const newProduct = await productService.createProduct(formData, image!);
      
      setSuccess('Produit ajouté avec succès !');
      
      // Réinitialiser le formulaire
      setFormData({
        name: '',
        description: '',
        price: 0,
        category_id: 0,
        stock: 0,
        delivery_fee: 0  // ✅ NOUVEAU
      });
      setImage(null);
      setPreview(null);
      
      // Rediriger vers le dashboard après 2 secondes
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (err: any) {
      console.error('Erreur lors de l\'ajout du produit:', err);
      
      if (err.response?.data?.detail) {
        setErrors([err.response.data.detail]);
      } else {
        setErrors(['Erreur lors de l\'ajout du produit. Veuillez réessayer.']);
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ MODIFIÉ - Calculer le pourcentage de completion avec delivery_fee
  const getCompletionPercentage = () => {
    const fields = [formData.name, formData.description, formData.price, formData.category_id, formData.stock, formData.delivery_fee, image];
    const completedFields = fields.filter(field => {
      if (field === null || field === undefined) return false;
      if (typeof field === 'string') return field.trim().length > 0;
      if (typeof field === 'number') return field >= 0;
      return true; // Pour image (File)
    }).length;
    return Math.round((completedFields / fields.length) * 100);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header avec progression */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-slate">Ajouter un produit</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Barre de progression */}
          <div className="bg-gray-200 rounded-full h-2">
            <div 
              className="bg-olive h-2 rounded-full transition-all duration-300"
              style={{ width: `${getCompletionPercentage()}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Progression: {getCompletionPercentage()}% complété
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Messages d'erreur */}
          {errors.length > 0 && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <h4 className="font-semibold mb-2">Erreurs à corriger :</h4>
              <ul className="list-disc list-inside">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Message de succès */}
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {success}
              </div>
              <p className="text-sm mt-1">Redirection vers le tableau de bord...</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Nom du produit — liste déroulante */}
            <div>
              <label className="block text-slate font-medium mb-2" htmlFor="name">
                Nom du produit *
              </label>
              <select
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
              >
                <option value="">Sélectionner un produit</option>
                {productTemplates.map(template => (
                  <option key={template.id} value={template.name}>
                    {template.name}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                <span>Votre produit n'apparaît pas dans la liste ?</span>
                <a
                  href="https://wa.me/22995040802"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-green-700 font-medium hover:underline"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Contactez-nous sur WhatsApp
                </a>
              </div>
            </div>
            
            {/* ✅ NOUVEAU - Prix et Frais de livraison côte à côte */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block text-slate font-medium mb-2" htmlFor="price">
                  Prix unitaire (FCFA) *
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price || ''}
                  onChange={handleChange}
                  required
                  min="1"
                  step="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                  placeholder="1000"
                />
              </div>
              
              {/* ✅ NOUVEAU - Champ frais de livraison */}
              <div>
                <label className="block text-slate font-medium mb-2" htmlFor="delivery_fee">
                  Frais de livraison (FCFA)
                </label>
                <input
                  type="number"
                  id="delivery_fee"
                  name="delivery_fee"
                  value={formData.delivery_fee || ''}
                  onChange={handleChange}
                  min="0"
                  step="50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                  placeholder="500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Laissez 0 pour une livraison gratuite
                </p>
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-slate font-medium mb-2" htmlFor="description">
                Description du produit *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                placeholder="Décrivez votre produit: origine, qualité, mode de culture, etc."
              />
              <p className="text-xs text-gray-500 mt-1">
                Caractères: {formData.description.length} (minimum 10 recommandé)
              </p>
            </div>
            
            {/* Catégorie et stock */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block text-slate font-medium mb-2" htmlFor="category_id">
                  Catégorie *
                </label>
                <select
                  id="category_id"
                  name="category_id"
                  value={formData.category_id || ''}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {categoryService.formatCategoryName(category)}
                      {category.description && ` - ${categoryService.getShortDescription(category, 30)}`}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-slate font-medium mb-2" htmlFor="stock">
                  Quantité disponible *
                </label>
                <input
                  type="number"
                  id="stock"
                  name="stock"
                  value={formData.stock || ''}
                  onChange={handleChange}
                  required
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta"
                  placeholder="Ex: 50"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nombre d'unités disponibles à la vente
                </p>
              </div>
            </div>
            
            {/* Upload d'image */}
            <div>
              <label className="block text-slate font-medium mb-2">
                Photo du produit *
              </label>
              <div className="mt-1 flex items-start space-x-4">
                {/* Aperçu de l'image */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300">
                    {preview ? (
                      <img src={preview} alt="Aperçu" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full w-full">
                        <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Zone d'upload */}
                <div className="flex-1">
                  <label htmlFor="file-upload" className="cursor-pointer bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                    <span>📷 Choisir une photo</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleImageChange}
                      required={!image}
                    />
                  </label>
                  <p className="mt-2 text-xs text-gray-500">
                    JPG, PNG, GIF ou WebP. Maximum 5MB.<br/>
                    L'image sera automatiquement redimensionnée pour un affichage optimal.
                  </p>
                  {image && (
                    <p className="mt-1 text-sm text-green-600">
                      ✓ {image.name} ({Math.round(image.size / 1024)} KB)
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* ✅ MODIFIÉ - Aperçu du produit avec frais de livraison */}
            {formData.name && formData.price && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-2">Aperçu :</h4>
                <div className="flex items-center space-x-3">
                  {preview && (
                    <img src={preview} alt="Aperçu produit" className="w-16 h-16 rounded object-cover" />
                  )}
                  <div>
                    <p className="font-semibold">{formData.name}</p>
                    <p className="text-terracotta font-bold">{formData.price} FCFA</p>
                    <p className="text-sm text-gray-600">Stock: {formData.stock} unités</p>
                    {/* ✅ NOUVEAU - Affichage des frais de livraison */}
                    <p className="text-sm text-blue-600">
                      Livraison: {formData.delivery_fee > 0 ? `${formData.delivery_fee} FCFA` : 'Gratuite'}
                    </p>
                    {/* ✅ NOUVEAU - Prix total si frais de livraison */}
                    {formData.delivery_fee > 0 && (
                      <p className="text-sm text-green-600 font-medium">
                        Total client: {formData.price + formData.delivery_fee} FCFA
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* ✅ NOUVEAU - Information sur les frais de livraison */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">À propos des frais de livraison</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Définissez des frais fixes par produit (ex: 500 FCFA)</li>
                      <li>Les frais s'ajoutent au prix du produit</li>
                      <li>Mettez 0 pour offrir la livraison gratuite</li>
                      <li>Les clients voient le total avant de commander</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex justify-between items-center pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              
              <button
                type="submit"
                disabled={loading || getCompletionPercentage() < 100}
                className="px-8 py-2 bg-olive text-white rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Publication...
                  </>
                ) : (
                  '🚀 Publier le produit'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProductPage;